import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// Translates an index future's live day-move onto the index's own price
// scale: previous close × (1 + the future's %). Used by the markets grid
// while the US market is closed.
//
// The future's ABSOLUTE level must never be shown as the index: every
// e-mini trades points off its underlying (basis), and NQUSD tracks the
// Nasdaq-100 while the card is the Composite — a different scale entirely.
// The percentage move is the transferable signal, so the implied level is
// always the index's previous close moved by that percentage.
//
// The index's LAST PRICE is the sanity check. The e-mini's % is measured
// from its own last settlement, and FMP rolls the index's previousClose on
// a different boundary, so for part of the evening the two references sit a
// whole session apart — yesterday's close moved by tonight's small % would
// erase the day's move. A genuine implied level can only be as far from the
// last price as the future has moved (plus a little basis drift); further
// than that and the references are out of phase.
//
// Returns null when either input is unusable or the references disagree —
// callers fall back to the index's own quote rather than showing a
// fabricated number.
const REFERENCE_DRIFT_TOLERANCE_PCT = 0.5;

export const impliedIndexLevel = (
  indexPreviousClose: number | null | undefined,
  futuresChangePercentage: number | null | undefined,
  indexPrice?: number | null
): number | null => {
  if (
    indexPreviousClose == null ||
    !Number.isFinite(indexPreviousClose) ||
    indexPreviousClose <= 0 ||
    futuresChangePercentage == null ||
    !Number.isFinite(futuresChangePercentage)
  ) {
    return null;
  }
  const implied = indexPreviousClose * (1 + futuresChangePercentage / 100);
  if (indexPrice != null && Number.isFinite(indexPrice) && indexPrice > 0) {
    const driftPct = (Math.abs(implied - indexPrice) / indexPrice) * 100;
    if (driftPct > Math.abs(futuresChangePercentage) + REFERENCE_DRIFT_TOLERANCE_PCT) {
      return null;
    }
  }
  return implied;
};

// CME Globex equity index futures trade Sunday 18:00 to Friday 17:00 ET,
// with a daily 17:00–18:00 ET maintenance break. Outside those hours the
// future's last print is stale and must not be presented as a live level.
// (CME holiday early closes are not modelled.)
const CME_DAILY_CLOSE_MINUTES = 17 * 60;
const CME_DAILY_REOPEN_MINUTES = 18 * 60;

export const isCmeTradingNow = (now: Date): boolean => {
  const newYork = dayjs(now).tz("America/New_York");
  const weekday = newYork.day();
  const minutes = newYork.hour() * 60 + newYork.minute();
  if (weekday === 6) return false;
  if (weekday === 0) return minutes >= CME_DAILY_REOPEN_MINUTES;
  if (weekday === 5) return minutes < CME_DAILY_CLOSE_MINUTES;
  return minutes < CME_DAILY_CLOSE_MINUTES || minutes >= CME_DAILY_REOPEN_MINUTES;
};
